import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Download, Printer, CheckCircle, Loader2, AlertCircle, Home, Calendar } from 'lucide-react';
import axios from 'axios';

// ====================================
// THEME & CONSTANTS
// ====================================

const theme = {
  primaryPink: '#FF1F8E',
  secondaryBlue: '#0066CC',
  govBlue: '#003B7A',
  white: '#FFFFFF',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray600: '#4B5563',
  gray700: '#374151',
  gray900: '#111827',
  success: '#10B981'
} as const;

// ====================================
// TYPE DEFINITIONS
// ====================================

interface BookingData {
  id: string;
  bookingNumber: string;
  applicantName: string;
  venueName: string;
  venueLocation: string;
  venueImage?: string;
  prefixDays: number;
  bookedFrom: string;
  bookedTo: string;
  suffixDays: number;
  status: string;
  totalAmount: number;
  rentAmount: number;
  securityDeposit: number;
  gst: number;
  prefixSuffixRent: number;
  venueType: 'party' | 'shooting' | 'event';
  eventName: string;
  mobile: string;
  email: string;
  gstNumber?: string;
  termsAndConditions: string[];
  paymentId?: string;
  bookingDate: string;
}

type LoadingState = 'loading' | 'success' | 'error';

// ====================================
// MAIN COMPONENT
// ====================================

const BookingReceipt: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // State
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Check if coming from payment success
  const fromPayment = searchParams.get('source') === 'payment';
  const orderId = searchParams.get('orderId');

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
  const BACKEND_BASE_URL = API_BASE_URL.replace('/api/v1', '');

  // ====================================
  // EFFECTS
  // ====================================

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    } else {
      setError('No booking ID provided');
      setLoadingState('error');
    }
  }, [bookingId]);

  // ====================================
  // API FUNCTIONS
  // ====================================

  const fetchBookingDetails = async () => {
    try {
      setLoadingState('loading');
      console.log('📥 Fetching booking details for:', bookingId);
      
      const response = await axios.get(`${API_BASE_URL}/bookings/public/${bookingId}/receipt`);
      
      if (response.data.success) {
        const booking = response.data.data;
        setBookingData(booking);
        setLoadingState('success');
        console.log('✅ Booking data loaded:', booking.bookingNumber);
      }
    } catch (err: any) {
      console.error('❌ Error fetching booking:', err);
      setError(err.response?.data?.message || 'Failed to load booking details');
      setLoadingState('error');
    }
  };

  // ====================================
  // HANDLERS
  // ====================================

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    if (!bookingId) {
      alert('Booking ID not found');
      return;
    }

    try {
      setIsDownloading(true);
      console.log('📄 Downloading PDF from backend...');
      
      const response = await axios.get(
        `${API_BASE_URL}/bookings/public/${bookingId}/receipt/pdf`,
        {
          responseType: 'blob',
          timeout: 30000,
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `HMDA_Receipt_${bookingData?.bookingNumber || bookingId}_${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ PDF downloaded successfully');
    } catch (error: any) {
      console.error('❌ Failed to download PDF:', error);
      
      let errorMessage = 'Failed to download PDF. ';
      
      if (error.code === 'ECONNABORTED') {
        errorMessage += 'Request timeout. Please try again.';
      } else if (error.response?.status === 404) {
        errorMessage += 'Receipt not found.';
      } else if (error.response?.status === 429) {
        errorMessage += 'Too many requests. Please wait a moment.';
      } else {
        errorMessage += 'Please try again or use the Print option.';
      }
      
      alert(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleReturnHome = () => {
    navigate('/');
  };

  const getImageUrl = (imagePath?: string): string => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return `${BACKEND_BASE_URL}${imagePath}`;
  };

  // ====================================
  // LOADING STATE
  // ====================================

  if (loadingState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-12 text-center shadow-xl max-w-md w-full">
          <Loader2 className="h-16 w-16 text-blue-600 animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Loading Receipt
          </h2>
          <p className="text-gray-600">
            Please wait while we fetch your booking details...
          </p>
        </div>
      </div>
    );
  }

  // ====================================
  // ERROR STATE
  // ====================================

  if (loadingState === 'error' || !bookingData) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-12 text-center shadow-xl max-w-md w-full">
          <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Booking Not Found
          </h2>
          <p className="text-gray-600 mb-8">
            {error || 'Unable to load booking details'}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/bookings')}
              className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              View My Bookings
            </button>
            <button
              onClick={handleReturnHome}
              className="bg-gray-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <Home size={18} />
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ====================================
  // MAIN RECEIPT RENDER
  // ====================================

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 font-sans">
      {/* ✅ SUCCESS BANNER - Show only when coming from payment */}
      {fromPayment && (
        <div className="no-print max-w-4xl mx-auto mb-6 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 shadow-2xl animate-slideDown">
          <div className="flex items-center justify-center gap-4 text-white mb-4">
            <CheckCircle size={40} className="flex-shrink-0" />
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">
                🎉 Payment Successful!
              </h2>
              <p className="text-sm md:text-base opacity-90 mt-1">
                Your booking has been confirmed • Order ID: {orderId}
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-6 py-2.5 bg-white/20 backdrop-blur-sm border-2 border-white/30 rounded-xl text-white font-semibold hover:bg-white/30 transition-all"
            >
              <Printer size={18} />
              Print Receipt
            </button>
            <button
              onClick={() => navigate('/bookings')}
              className="flex items-center gap-2 px-6 py-2.5 bg-white/20 backdrop-blur-sm border-2 border-white/30 rounded-xl text-white font-semibold hover:bg-white/30 transition-all"
            >
              <Calendar size={18} />
              View All Bookings
            </button>
            <button
              onClick={handleReturnHome}
              className="flex items-center gap-2 px-6 py-2.5 bg-white/20 backdrop-blur-sm border-2 border-white/30 rounded-xl text-white font-semibold hover:bg-white/30 transition-all"
            >
              <Home size={18} />
              Return Home
            </button>
          </div>
        </div>
      )}

      {/* ✅ Action Buttons - Updated with Home button */}
      <div className="no-print max-w-4xl mx-auto mb-6 flex flex-wrap justify-between items-center gap-3">
        {/* ✅ Return Home button (left side) */}
        <button
          onClick={handleReturnHome}
          className="flex items-center gap-2 px-5 py-2.5 bg-gray-600 text-white rounded-xl font-semibold hover:bg-gray-700 transition-colors shadow-lg"
        >
          <Home size={18} />
          Return Home
        </button>

        {/* Action buttons (right side) */}
        <div className="flex gap-3">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-lg"
          >
            <Printer size={18} />
            Print
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={isDownloading}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-colors shadow-lg ${
              isDownloading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {isDownloading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download size={18} />
                Download PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Receipt Container */}
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b-4 border-pink-500 p-8">
          <div className="flex items-center justify-between gap-6">
            {/* HMDA Logo */}
            <img 
              src={`${process.env.PUBLIC_URL}/images/hmdalogo.png`}
              alt="HMDA Logo"
              className="h-20 w-auto object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        </header>

        {/* Body */}
        <div className="p-8 md:p-12">
          {/* Success Badge */}
          <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-xl border-2 border-green-200 font-bold mb-8">
            <CheckCircle size={20} />
            Booking Confirmed
          </div>

          {/* Venue Image */}
          {bookingData.venueImage && (
            <div className="mb-8">
              <img 
                src={getImageUrl(bookingData.venueImage)}
                alt={bookingData.venueName}
                className="w-full md:w-96 h-64 object-cover rounded-xl shadow-lg mx-auto border-4 border-gray-200"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <p className="text-center text-lg text-gray-800 mt-3 font-bold">
                📍 {bookingData.venueName}
              </p>
              <p className="text-center text-sm text-gray-600">
                {bookingData.venueLocation}
              </p>
            </div>
          )}

          {/* Booking Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 rounded-xl p-6 mb-8">
            <InfoField label="Booked By" value={bookingData.applicantName} />
            <InfoField 
              label="Booking Number" 
              value={bookingData.bookingNumber} 
              valueClass="text-pink-600 font-bold"
            />
            <InfoField label="Venue" value={bookingData.venueName} />
            <InfoField label="Event Name" value={bookingData.eventName} />
            <InfoField label="Prefix Days" value={bookingData.prefixDays.toString()} />
            <InfoField label="Booked From" value={bookingData.bookedFrom} />
            <InfoField label="Booked To" value={bookingData.bookedTo} />
            <InfoField label="Suffix Days" value={bookingData.suffixDays.toString()} />
            <InfoField 
              label="Status" 
              value={bookingData.status.toUpperCase()} 
              valueClass="text-green-600 font-bold"
            />
            <InfoField label="Mobile" value={bookingData.mobile} />
            <InfoField label="Email" value={bookingData.email} />
            <InfoField label="GST Number" value={bookingData.gstNumber || 'N/A'} />
            <div className="md:col-span-2">
              <InfoField 
                label="Booking Amount" 
                value={`₹${bookingData.totalAmount.toLocaleString('en-IN')}`}
                valueClass="text-2xl font-black text-pink-600"
              />
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-black text-gray-900 mb-4">
              Payment Breakdown
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between text-gray-700">
                <span>Rent Amount:</span>
                <span className="font-bold">₹{bookingData.rentAmount.toLocaleString('en-IN')}</span>
              </div>
              {bookingData.prefixSuffixRent > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Prefix/Suffix Rent:</span>
                  <span className="font-bold">₹{bookingData.prefixSuffixRent.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-700">
                <span>Security Deposit (Refundable):</span>
                <span className="font-bold">₹{bookingData.securityDeposit.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-gray-700 border-b border-gray-300 pb-3">
                <span>GST (18%):</span>
                <span className="font-bold">₹{bookingData.gst.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-gray-900 text-lg pt-2">
                <span className="font-black">Total Amount:</span>
                <span className="font-black text-pink-600">
                  ₹{bookingData.totalAmount.toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </div>

          {/* Terms & Conditions */}
          <section>
            <h2 className="text-2xl font-black text-gray-900 mb-6 pb-4 border-b-4 border-pink-500">
              Terms & Conditions
            </h2>

            <div className="border-2 border-gray-900 rounded-xl overflow-hidden mb-6">
              <div className="bg-gray-900 text-white px-4 py-3">
                <h3 className="font-bold">Terms & Condition</h3>
              </div>
              <div>
                {bookingData.termsAndConditions.map((term, index) => (
                  <div 
                    key={index}
                    className={`px-4 py-3 text-sm leading-relaxed ${
                      index > 0 ? 'border-t border-gray-200' : ''
                    }`}
                  >
                    {index + 1}. {term}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ✅ Bottom Return Home Button (before footer) */}
          <div className="no-print text-center mt-8 mb-4">
            <button
              onClick={handleReturnHome}
              className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg transform hover:scale-105"
            >
              <Home size={20} />
              Return to Home
            </button>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-white text-center py-6 px-8">
          <p className="text-sm mb-2">
            Generated on: {new Date().toLocaleString('en-IN', { 
              dateStyle: 'medium', 
              timeStyle: 'short' 
            })}
          </p>
          <p className="text-sm mb-3">
            © 2025 Hyderabad Metropolitan Development Authority. All rights reserved.
          </p>
          <p className="text-xs opacity-80">
            https://bpp.hmda.gov.in
          </p>
        </footer>
      </div>

      {/* Print Styles */}
      <style>{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .animate-slideDown {
          animation: slideDown 0.5s ease-out;
        }
        
        @media print {
          body { 
            margin: 0; 
            padding: 0;
            background: white;
          }
          .no-print { 
            display: none !important; 
          }
          @page { 
            margin: 1cm;
            size: A4;
          }
        }
      `}</style>
    </div>
  );
};

// ====================================
// SUB-COMPONENTS
// ====================================

interface InfoFieldProps {
  label: string;
  value: string;
  valueClass?: string;
}

const InfoField: React.FC<InfoFieldProps> = ({ label, value, valueClass = '' }) => (
  <div>
    <p className="text-xs text-gray-500 font-semibold mb-1">{label}</p>
    <p className={`text-base font-bold text-gray-900 ${valueClass}`}>{value}</p>
  </div>
);

export default BookingReceipt;